import { ChangeEvent, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import useAuth from "../../hooks/useAuth";
import {
  getRecruitingContacts,
  importRecruitingContacts,
} from "../../api/recruitingContactsService";
import PersistedDataGrid from "../../components/PersistedDataGrid/PersistedDataGrid";
import { recruitingContactsTableColumns } from "../../data/FormColumns/Recruiting/recruitingContactsTableColumns";

const ApplicantsPage = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [contactDate, setContactDate] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<"success" | "error">(
    "success",
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["recruitingContacts", fromDate, toDate, status],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getRecruitingContacts(
        token,
        fromDate || undefined,
        toDate || undefined,
        status || undefined,
      );
    },
    enabled: !!token,
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => {
      if (!token) throw new Error("No token");
      return importRecruitingContacts(token, file, contactDate || undefined);
    },
    onSuccess: (result) => {
      setFeedbackType("success");
      setFeedback(
        `Import complete. Imported: ${result.importedCount}, skipped: ${result.skippedCount}.`,
      );
      queryClient.invalidateQueries({ queryKey: ["recruitingContacts"] });
      queryClient.invalidateQueries({
        queryKey: ["recruitingContactStatistics"],
      });
    },
    onError: (mutationError: any) => {
      setFeedbackType("error");
      setFeedback(
        mutationError?.response?.data ||
          mutationError?.message ||
          "Unable to import contacts.",
      );
    },
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    importMutation.mutate(file);
    event.target.value = "";
  };

  return (
    <Box className="incidents-page">
      <Stack spacing={2} mb={2} sx={{ width: "100%", minWidth: 0 }}>
        <Typography variant="h5" fontWeight={700}>
          Applicants
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(160px, 1fr)) auto",
            },
            alignItems: "start",
            gap: 2,
          }}
        >
          <TextField
            label="From date"
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 0 }}
          />
          <TextField
            label="To date"
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 0 }}
          />
          <TextField
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            placeholder="e.g. Hired"
            fullWidth
            sx={{ minWidth: 0 }}
          />
          <TextField
            label="Import contact date"
            type="date"
            value={contactDate}
            onChange={(event) => setContactDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Optional"
            fullWidth
            sx={{ minWidth: 0 }}
          />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importMutation.isPending}
          >
            Upload CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFileChange}
          />
        </Box>
        {feedback && <Alert severity={feedbackType}>{feedback}</Alert>}
      </Stack>

      {isLoading ? (
        <Box
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          display="flex"
          justifyContent="center"
          py={6}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          {(error as Error)?.message || "Unable to load applicants."}
        </Alert>
      ) : (
        <Box sx={{ height: 760, width: "100%", minWidth: 0 }}>
          <PersistedDataGrid
            tableName="recruitingApplicants"
            mobileCompactView
            compactViewConfig={{
              primaryField: "name",
              statusField: "status",
              detailFields: [
                "contactDate",
                "primaryPhone",
                "secondaryPhone",
                "statusReason",
              ],
            }}
            rows={data || []}
            columns={recruitingContactsTableColumns}
            sx={{ height: 700 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ApplicantsPage;
